/**
 * Manufacturing Service API Client
 * Provides master data APIs for manufacturing operations
 *
 * @description
 * This service provides read-only access to master data:
 * - Raw material types
 * - Dung types
 * - QC grades
 * - Product types (hierarchical)
 * - Location data (states, districts, areas)
 *
 * All endpoints are cached on the backend for optimal performance.
 *
 * @author SAUBHAGYA Development Team
 * @since Story 1-6
 */

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface RawMaterialTypeDTO {
  id: number;
  name: string;
  code: string;
  isActive: boolean;
}

export interface DungTypeDTO {
  id: number;
  name: string;
  code: string;
}

export interface QcGradeDTO {
  id: number;
  grade: string;
  name: string;
  moistureMax: number;
  purityMin: number;
  priceMultiplier: number;
}

export interface ProductTypeDTO {
  id: number;
  name: string;
  code: string;
  parentId: number | null;
  children: ProductTypeDTO[];
}

export interface DungProductDTO {
  id: number;
  productName: string;
  category: string;
  description: string | null;
  price: number | null;
  unit: string | null;
  status: string;
}

export interface LocationStateDTO {
  id: number;
  name: string;
  code: string;
}

export interface LocationDistrictDTO {
  id: number;
  name: string;
  code: string;
  stateId: number;
}

export interface LocationAreaDTO {
  id: number;
  name: string;
  code: string;
  districtId: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string | null;
}

export interface CreateTransactionRequest {
  gaushalaId: number;
  rawMaterialTypeId: number;
  dungTypeId?: number;
  quantityKg: number;
  notes?: string;
}

export interface UpdateTransactionRequest {
  quantityKg?: number;
  rawMaterialTypeId?: number;
  dungTypeId?: number;
  grade?: string;
}

export interface QCVerificationDTO {
  id: number;
  moisturePercent: number;
  purityPercent: number;
  dungType: DungTypeDTO;
  grade: string;
  gradeName: string;
  priceMultiplier: number;
  verifiedBy: number;
  verifiedAt: string;
  notes: string | null;
}

export interface QCVerificationRequest {
  moisturePercent: number;
  purityPercent: number;
  dungTypeId: number;
  notes?: string;
}

export interface GaushalaInfo {
  id: number;
  name: string;
  location?: string;
}

export interface TransactionDTO {
  id: number;
  batchCode: string;
  gaushala: GaushalaInfo;
  gaushalaId?: number;
  gaushalaName?: string;
  rawMaterialType: string;
  rawMaterialTypeId?: number;
  rawMaterialTypeName?: string;
  dungType: string | null;
  dungTypeId?: number | null;
  dungTypeName?: string | null;
  quantityKg: number;
  quantityAvailableKg?: number;
  notes: string | null;
  status: string;
  createdAt: string;
  createdBy?: string;
  qc?: QCVerificationDTO | null;
}

export interface PageResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface PaymentDTO {
  id: number;
  transactionId: number;
  batchCode: string;
  gaushalaId: number;
  gaushalaName: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  gatewayType: string;
  gatewayTransactionId?: string;
  initiatedAt: string;
  completedAt?: string;
  failureReason?: string;
}

export interface PaymentDetailDTO extends PaymentDTO {
  timeline: PayoutTimelineDTO[];
  gatewayResponse?: string;
}

export interface PayoutTimelineDTO {
  previousStatus?: string;
  newStatus: string;
  notes?: string;
  gatewayResponse?: string;
  createdAt: string;
}

export interface PaymentSummaryDTO {
  totalCompletedAmount: number;
  pendingCount: number;
  processingCount: number;
  completedCount: number;
  failedCount: number;
}

export interface ApproveTransactionRequest {
  pricePerKg: number;
  notes?: string;
}

// ============================================
// PRODUCTION BATCH TYPES
// ============================================

export interface ProductionBatchDTO {
  id: number;
  batchCode: string;
  manufacturingUnitId: number;
  manufacturingUnitName: string;
  dungProductId: number;
  dungProductName: string;
  status: 'CREATED' | 'PROCESSING' | 'MANUFACTURED' | 'READY_FOR_SALE' | 'CANCELLED' | 'ALLOCATED' | 'SOLD' | 'CONSUMED';
  statusDisplayName: string;
  totalInputQuantityKg: number;
  outputQuantityKg: number | null;
  startedAt: string | null;
  completedAt: string | null;
  notes: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  inputs?: BatchInputDTO[];
  timeline?: BatchTimelineDTO[];
}

export interface BatchInputDTO {
  id: number;
  transactionId: number;
  transactionBatchCode: string;
  rawMaterialTypeName: string;
  supplierName: string;
  consumedQuantityKg: number;
  consumedAt: string;
}

export interface BatchTimelineDTO {
  id: number;
  status: string;
  statusDisplayName: string;
  changedBy: number;
  changedAt: string;
  notes: string | null;
}

export interface AvailableTransactionDTO {
  id: number;
  batchCode: string;
  rawMaterialTypeName: string;
  supplierName: string;
  gaushalaPanchayatName: string;
  totalQuantityKg: number;
  consumedQuantityKg: number;
  availableQuantityKg: number;
  qcGrade: string;
  approvedAt: string;
}

export interface CreateBatchRequest {
  manufacturingUnitId: number;
  dungProductId: number;
  inputs: { transactionId: number; quantityKg: number }[];
  notes?: string;
}

export interface CompleteBatchRequest {
  outputQuantityKg: number;
  notes?: string;
}

export interface ProductionBatchSummary {
  totalBatches: number;
  createdCount: number;
  processingCount: number;
  manufacturedCount: number;
  readyForSaleCount: number;
  cancelledCount: number;
  totalInputQuantityKg: number;
  totalOutputQuantityKg: number;
}

export interface PaginatedResponse<T> {
  content: T[];
  page?: number; // Legacy field name
  number?: number; // Spring Boot field name (actual response field)
  size: number;
  totalElements: number;
  totalPages: number;
}

// Helper to get page number from response (handles both naming conventions)
function getPageNumber(response: any): number {
  return response.page ?? response.number ?? 0;
}

// ============================================
// TRANSACTION SUMMARY TYPES
// ============================================

export interface TransactionSummaryResponse {
  total: number;
  pendingQc: number;
  qcVerified: number;
  approved: number;
  readyForProduction: number;
  totalQuantityKg: number;
}

// ============================================
// DASHBOARD TYPES
// ============================================

export interface DashboardKpiResponse {
  period: {
    from: string;
    to: string;
  };
  transactions: {
    total: number;
    pendingQc: number;
    qcVerified: number;
    approved: number;
    readyForProduction: number;
  };
  production: {
    totalBatches: number;
    inProgress: number;
    completed: number;
    totalOutputKg: number;
  };
  payments: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    totalAmountPaid: number;
  };
  rawMaterials: {
    totalReceivedKg: number;
    averageGrade: string;
    gradeDistribution: Record<string, number>;
  };
}

export interface ChartDataResponse {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
  }>;
}

export interface PaymentChartResponse {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
  }>;
  summary: {
    totalCompletedAmount: number;
    completedCount: number;
    pendingCount: number;
    processingCount: number;
    failedCount: number;
  };
}

// ============================================
// CONFIGURATION
// ============================================

import { getServiceUrl } from '../../config/serviceConfig';

const API_BASE_URL = getServiceUrl('manufacturing');
const GAUSHALA_API_BASE_URL = getServiceUrl('gaushala');
const MASTER_DATA_PATH = '/api/v1/master';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for API errors
 */
export class ManufacturingApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errors?: string[]
  ) {
    super(message);
    this.name = 'ManufacturingApiError';
  }
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();

      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }
      if (errorData.errors && Array.isArray(errorData.errors)) {
        return errorData.errors.join(', ');
      }
    }
    return `HTTP ${response.status}: ${response.statusText}`;
  } catch {
    return `HTTP ${response.status}: ${response.statusText}`;
  }
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorMessage = await parseErrorResponse(response);
    throw new ManufacturingApiError(errorMessage, response.status);
  }

  return response.json();
}

// ============================================
// GAUSHALA SEARCH UTILITY (frontend-side resolution)
// ============================================

/**
 * Search gaushala IDs by name directly from gaushala-service.
 * This bypasses the broken backend-to-backend cross-service call
 * by resolving gaushala names to IDs on the frontend.
 *
 * @param searchTerm - Partial gaushala name to search
 * @returns Array of matching gaushala IDs
 */
export async function searchGaushalaIds(searchTerm: string): Promise<number[]> {
  try {
    if (!searchTerm || !searchTerm.trim()) return [];

    const url = `${GAUSHALA_API_BASE_URL}/api/v1/gaushalas/search/ids?searchTerm=${encodeURIComponent(searchTerm.trim())}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      console.warn('[searchGaushalaIds] Failed to search gaushalas:', response.status);
      return [];
    }

    const data = await response.json();
    if (data?.data && Array.isArray(data.data)) {
      return data.data.map((g: { id: number }) => g.id);
    }
    return [];
  } catch (error) {
    console.warn('[searchGaushalaIds] Error searching gaushalas:', error);
    return [];
  }
}

// ============================================
// API METHODS
// ============================================

/**
 * Manufacturing API client
 */
export const manufacturingApi = {
  /**
   * Get all active raw material types
   * @returns Promise with list of active raw material types
   */
  getRawMaterialTypes: async (): Promise<RawMaterialTypeDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/raw-material-types`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<RawMaterialTypeDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch raw material types:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch raw material types'
      );
    }
  },

  /**
   * Get all dung types
   * @returns Promise with list of all dung types
   */
  getDungTypes: async (): Promise<DungTypeDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/dung-types`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<DungTypeDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch dung types:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch dung types'
      );
    }
  },

  /**
   * Get all QC grades ordered by grade
   * @returns Promise with list of QC grades
   */
  getQcGrades: async (): Promise<QcGradeDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/qc-grades`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<QcGradeDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch QC grades:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch QC grades'
      );
    }
  },

  /**
   * Get all product types in hierarchical structure
   * @deprecated Use getDungProducts() instead
   * @returns Promise with list of root-level product types with children
   */
  getProductTypes: async (): Promise<ProductTypeDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/product-types`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<ProductTypeDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch product types:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch product types'
      );
    }
  },

  /**
   * Get all active dung products for production batch creation
   * @returns Promise with list of active dung products
   */
  getDungProducts: async (): Promise<DungProductDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/dung-products`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<DungProductDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch dung products:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch dung products'
      );
    }
  },

  /**
   * Get all states
   * @returns Promise with list of all states
   */
  getStates: async (): Promise<LocationStateDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/locations/states`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<LocationStateDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch states:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch states'
      );
    }
  },

  /**
   * Get all districts for a given state
   * @param stateId - The state ID
   * @returns Promise with list of districts
   */
  getDistricts: async (stateId: number): Promise<LocationDistrictDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/locations/districts/${stateId}`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<LocationDistrictDTO[]>(response);
    } catch (error) {
      console.error(`Failed to fetch districts for state ${stateId}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch districts'
      );
    }
  },

  /**
   * Get all areas for a given district
   * @param districtId - The district ID
   * @returns Promise with list of areas
   */
  getAreas: async (districtId: number): Promise<LocationAreaDTO[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${MASTER_DATA_PATH}/locations/areas/${districtId}`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<LocationAreaDTO[]>(response);
    } catch (error) {
      console.error(`Failed to fetch areas for district ${districtId}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch areas'
      );
    }
  },

  // ============================================
  // TRANSACTION OPERATIONS
  // ============================================

  /**
   * Create a new raw material transaction
   * @param data - Transaction creation data
   * @returns Promise with created transaction
   */
  createTransaction: async (data: CreateTransactionRequest): Promise<TransactionDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<TransactionDTO>(response);
    } catch (error) {
      console.error('Failed to create transaction:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to create transaction'
      );
    }
  },

  /**
   * Update an UP_COMING transaction
   * @param transactionId - Transaction ID
   * @param data - Update data (quantityKg, rawMaterialTypeId, dungTypeId, grade)
   * @returns Promise with updated transaction
   */
  updateTransaction: async (
    transactionId: number,
    data: UpdateTransactionRequest
  ): Promise<TransactionDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions/${transactionId}`,
        {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<TransactionDTO>(response);
    } catch (error) {
      console.error(`Failed to update transaction ${transactionId}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to update transaction'
      );
    }
  },

  /**
   * Approve a driver collection (UP_COMING → INITIALIZED)
   * @param transactionId - Transaction ID
   * @returns Promise with updated transaction
   */
  approveDriverCollection: async (transactionId: number): Promise<TransactionDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions/${transactionId}/approve-collection`,
        {
          method: 'PUT',
          headers: getHeaders()
        }
      );

      return handleResponse<TransactionDTO>(response);
    } catch (error) {
      console.error(`Failed to approve driver collection ${transactionId}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to approve driver collection'
      );
    }
  },

  /**
   * Get transactions with optional filters
   * @param filters - Optional filters (status, page, size)
   * @returns Promise with paginated transactions
   */
  getTransactions: async (filters?: {
    status?: string | string[];
    gaushalaSearch?: string;
    gaushalaIds?: number[];
    grade?: string;
    materialTypeId?: string | number;
    fromDate?: string;
    toDate?: string;
    page?: number;
    size?: number;
  }): Promise<PageResponse<TransactionDTO>> => {
    try {
      const params = new URLSearchParams();

      // Handle status (can be array or string)
      if (filters?.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        console.log('[manufacturingApi] Status filter input:', filters.status, 'isArray:', Array.isArray(filters.status));
        console.log('[manufacturingApi] Processing statuses:', statuses);
        statuses.forEach(s => {
          console.log('[manufacturingApi] Appending status:', s);
          params.append('status', s);
        });
      }

      // Send gaushalaIds (resolved on frontend) - takes priority over gaushalaSearch
      if (filters?.gaushalaIds && filters.gaushalaIds.length > 0) {
        filters.gaushalaIds.forEach(id => params.append('gaushalaIds', id.toString()));
      } else if (filters?.gaushalaSearch) {
        params.append('gaushalaSearch', filters.gaushalaSearch);
      }
      if (filters?.grade) params.append('grade', filters.grade);
      if (filters?.materialTypeId) params.append('materialTypeId', String(filters.materialTypeId));
      if (filters?.fromDate) params.append('fromDate', filters.fromDate);
      if (filters?.toDate) params.append('toDate', filters.toDate);
      if (filters?.page !== undefined) params.append('page', filters.page.toString());
      if (filters?.size !== undefined) params.append('size', filters.size.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/v1/transactions${queryString ? `?${queryString}` : ''}`;

      console.log('[manufacturingApi] Final API URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
        cache: 'no-store',
      });

      return handleResponse<PageResponse<TransactionDTO>>(response);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch transactions'
      );
    }
  },

  /**
   * Get transaction summary metrics for stat cards
   * Returns all-time counts by status and total quantity
   * @returns Promise with transaction metrics
   */
  getTransactionSummary: async (): Promise<TransactionSummaryResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions/summary`,
        {
          method: 'GET',
          headers: getHeaders(),
          cache: 'no-store',
        }
      );

      return handleResponse<TransactionSummaryResponse>(response);
    } catch (error) {
      console.error('Failed to fetch transaction summary:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch transaction summary'
      );
    }
  },

  /**
   * Get a single transaction by ID
   * @param id - Transaction ID
   * @returns Promise with transaction details
   */
  getTransactionById: async (id: number): Promise<TransactionDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions/${id}`,
        {
          method: 'GET',
          headers: getHeaders(),
          cache: 'no-store',
        }
      );

      return handleResponse<TransactionDTO>(response);
    } catch (error) {
      console.error(`Failed to fetch transaction ${id}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch transaction'
      );
    }
  },

  /**
   * Submit QC verification for a transaction
   * @param transactionId - Transaction ID
   * @param data - QC verification data
   * @returns Promise with updated transaction including QC data
   */
  submitQCVerification: async (
    transactionId: number,
    data: QCVerificationRequest
  ): Promise<TransactionDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions/${transactionId}/qc`,
        {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<TransactionDTO>(response);
    } catch (error) {
      console.error(`Failed to submit QC verification for transaction ${transactionId}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to submit QC verification'
      );
    }
  },

  // ============================================
  // PAYMENT OPERATIONS
  // ============================================

  /**
   * Get payments with optional filters
   * @param params - Optional filters (status, gaushalaSearch, from, to, page, size)
   * @returns Promise with paginated payments
   */
  getPayments: async (params?: {
    status?: string;
    gaushalaSearch?: string;
    gaushalaIds?: number[];
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  }): Promise<PageResponse<PaymentDTO>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      // Send gaushalaIds (resolved on frontend) - takes priority over gaushalaSearch
      if (params?.gaushalaIds && params.gaushalaIds.length > 0) {
        params.gaushalaIds.forEach(id => queryParams.append('gaushalaIds', id.toString()));
      } else if (params?.gaushalaSearch) {
        queryParams.append('gaushalaSearch', params.gaushalaSearch);
      }
      if (params?.from) queryParams.append('fromDate', params.from);
      if (params?.to) queryParams.append('toDate', params.to);
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.size !== undefined) queryParams.append('size', params.size.toString());

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/v1/payments${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<PageResponse<PaymentDTO>>(response);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch payments'
      );
    }
  },

  /**
   * Get a single payment by ID
   * @param id - Payment ID
   * @returns Promise with payment details including timeline
   */
  getPayment: async (id: number): Promise<PaymentDetailDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/payments/${id}`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<PaymentDetailDTO>(response);
    } catch (error) {
      console.error(`Failed to fetch payment ${id}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch payment'
      );
    }
  },

  /**
   * Get payment summary statistics
   * @returns Promise with payment summary data
   */
  getPaymentSummary: async (): Promise<PaymentSummaryDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/payments/summary`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<PaymentSummaryDTO>(response);
    } catch (error) {
      console.error('Failed to fetch payment summary:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch payment summary'
      );
    }
  },

  /**
   * Approve a transaction and initiate payment
   * @param transactionId - Transaction ID
   * @param data - Approval data (pricePerKg, notes)
   * @returns Promise with updated transaction
   */
  approveTransaction: async (
    transactionId: number,
    data: ApproveTransactionRequest
  ): Promise<TransactionDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/transactions/${transactionId}/approve`,
        {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<TransactionDTO>(response);
    } catch (error) {
      console.error(`Failed to approve transaction ${transactionId}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to approve transaction'
      );
    }
  },

  // ============================================
  // PRODUCTION BATCH OPERATIONS
  // ============================================

  /**
   * Get production batches with optional filters
   * @param params - Optional filters
   * @returns Promise with paginated production batches
   */
  getProductionBatches: async (params?: {
    page?: number;
    size?: number;
    manufacturingUnitId?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<PaginatedResponse<ProductionBatchDTO>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.size !== undefined) queryParams.append('size', params.size.toString());
      if (params?.manufacturingUnitId) queryParams.append('manufacturingUnitId', params.manufacturingUnitId.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);
      if (params?.search) queryParams.append('search', params.search);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/v1/production-batches${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<PaginatedResponse<ProductionBatchDTO>>(response);
    } catch (error) {
      console.error('Failed to fetch production batches:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch production batches'
      );
    }
  },

  /**
   * Get a single production batch by ID
   * @param id - Production batch ID
   * @returns Promise with production batch details
   */
  getProductionBatch: async (id: number): Promise<ProductionBatchDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/production-batches/${id}`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );

      return handleResponse<ProductionBatchDTO>(response);
    } catch (error) {
      console.error(`Failed to fetch production batch ${id}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch production batch'
      );
    }
  },

  /**
   * Create a new production batch
   * @param data - Batch creation data
   * @returns Promise with created production batch
   */
  createProductionBatch: async (data: CreateBatchRequest): Promise<ProductionBatchDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/production-batches`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<ProductionBatchDTO>(response);
    } catch (error) {
      console.error('Failed to create production batch:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to create production batch'
      );
    }
  },

  /**
   * Start a production batch
   * @param id - Production batch ID
   * @returns Promise with updated production batch
   */
  startProductionBatch: async (id: number): Promise<ProductionBatchDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/production-batches/${id}/start`,
        {
          method: 'PUT',
          headers: getHeaders()
        }
      );

      return handleResponse<ProductionBatchDTO>(response);
    } catch (error) {
      console.error(`Failed to start production batch ${id}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to start production batch'
      );
    }
  },

  /**
   * Complete a production batch
   * @param id - Production batch ID
   * @param data - Completion data (output quantity, notes)
   * @returns Promise with updated production batch
   */
  completeProductionBatch: async (
    id: number,
    data: CompleteBatchRequest
  ): Promise<ProductionBatchDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/production-batches/${id}/complete`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(data)
        }
      );

      return handleResponse<ProductionBatchDTO>(response);
    } catch (error) {
      console.error(`Failed to complete production batch ${id}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to complete production batch'
      );
    }
  },

  /**
   * Cancel a production batch
   * @param id - Production batch ID
   * @param reason - Cancellation reason
   * @returns Promise with updated production batch
   */
  cancelProductionBatch: async (id: number, reason?: string): Promise<ProductionBatchDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/production-batches/${id}/cancel`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ notes: reason })
        }
      );

      return handleResponse<ProductionBatchDTO>(response);
    } catch (error) {
      console.error(`Failed to cancel production batch ${id}:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to cancel production batch'
      );
    }
  },

  /**
   * Get available transactions for a manufacturing unit
   * @param manufacturingUnitId - Optional manufacturing unit ID. If not provided, backend derives from JWT.
   * @returns Promise with list of available transactions
   */
  getAvailableTransactions: async (manufacturingUnitId?: number): Promise<AvailableTransactionDTO[]> => {
    try {
      const queryParams = new URLSearchParams();
      if (manufacturingUnitId) {
        queryParams.append('manufacturingUnitId', manufacturingUnitId.toString());
      }

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/v1/production-batches/available-transactions${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<AvailableTransactionDTO[]>(response);
    } catch (error) {
      console.error('Failed to fetch available transactions:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch available transactions'
      );
    }
  },

  /**
   * Get production batch summary statistics
   * @param manufacturingUnitId - Optional manufacturing unit ID filter
   * @returns Promise with production batch summary
   */
  getProductionBatchSummary: async (manufacturingUnitId?: number): Promise<ProductionBatchSummary> => {
    try {
      const queryParams = new URLSearchParams();
      if (manufacturingUnitId) queryParams.append('manufacturingUnitId', manufacturingUnitId.toString());

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/v1/production-batches/summary${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<ProductionBatchSummary>(response);
    } catch (error) {
      console.error('Failed to fetch production batch summary:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch production batch summary'
      );
    }
  },

  // ============================================
  // DASHBOARD OPERATIONS
  // ============================================

  /**
   * Get dashboard KPIs for a given date range
   * @param params - Date range parameters (from, to)
   * @returns Promise with dashboard KPI data
   */
  getDashboardKpis: async (params: { from: string; to: string }): Promise<DashboardKpiResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('from', params.from);
      queryParams.append('to', params.to);

      const url = `${API_BASE_URL}/api/v1/dashboard/kpis?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<DashboardKpiResponse>(response);
    } catch (error) {
      console.error('Failed to fetch dashboard KPIs:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch dashboard KPIs'
      );
    }
  },

  /**
   * Get production chart data
   * @param params - Date range and interval parameters
   * @returns Promise with chart data
   */
  getProductionChart: async (params: {
    from: string;
    to: string;
    interval: string;
  }): Promise<ChartDataResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('from', params.from);
      queryParams.append('to', params.to);
      queryParams.append('interval', params.interval);

      const url = `${API_BASE_URL}/api/v1/dashboard/charts/production?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<ChartDataResponse>(response);
    } catch (error) {
      console.error('Failed to fetch production chart:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch production chart'
      );
    }
  },

  /**
   * Get payment chart data
   * @param params - Date range and interval parameters
   * @returns Promise with payment chart data including summary
   */
  getPaymentChart: async (params: {
    from: string;
    to: string;
    interval: string;
  }): Promise<PaymentChartResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('from', params.from);
      queryParams.append('to', params.to);
      queryParams.append('interval', params.interval);

      const url = `${API_BASE_URL}/api/v1/dashboard/charts/payments?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return handleResponse<PaymentChartResponse>(response);
    } catch (error) {
      console.error('Failed to fetch payment chart:', error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to fetch payment chart'
      );
    }
  },

  /**
   * Mark a production batch as ready for sale
   * Transitions batch from MANUFACTURED to READY_FOR_SALE status
   *
   * @param id - Batch ID
   * @param notes - Optional notes about marking ready for sale
   * @returns Promise with updated batch details
   */
  markProductionBatchReadyForSale: async (
    id: number,
    notes?: string
  ): Promise<ProductionBatchDTO> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/production-batches/${id}/ready-for-sale`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ notes })
        }
      );

      return handleResponse<ProductionBatchDTO>(response);
    } catch (error) {
      console.error(`Failed to mark batch ${id} ready for sale:`, error);
      if (error instanceof ManufacturingApiError) {
        throw error;
      }
      throw new ManufacturingApiError(
        error instanceof Error ? error.message : 'Failed to mark batch ready for sale'
      );
    }
  }
};

export default manufacturingApi;
